import { type FC, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import VerticalStepForm from '@/components/UI/VerticalStepForm/VerticalStepForm';
import type { LoadOptionsResult, StepConfig } from '@/components/UI/VerticalStepForm/VerticalStepForm.model';
import { _isNil } from '@/littledash';
import { ApiService, ResponseType } from '@/support/ApiService';
import { useLegacyApiHook } from '@/support/Hooks/api/useLegacyApiHook';
import type { DatabaseId, DataWrapper } from '@/model/Common.model';
import type { AgentAttachmentBatchApiId } from '@/model/Agent.model';
import type { StudyForm } from '@/model/StudyForm.model';
import InVivoError from '@/model/InVivoError';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { web as webRoute } from '@/support/route';
import { agentUrlSlugMap } from '@/components/Agents/AgentUtils';

const DOCX_MIME = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
const DOCX_ACCEPT = `.docx,${DOCX_MIME}`;

type StudyProtocolParserSetupValues = {
  form_id: DatabaseId | null;
  // UploadField clears the value to null on error/remove; reflect that so a
  // null can't be silently treated as a string on submit. Holds the opaque
  // batch_api_id of the uploaded protocol — never a key or URL.
  protocol_batch_api_id: AgentAttachmentBatchApiId | null;
};

// TODO: presign + PUT + kickoff are inlined here, matching the other agent
// setup forms. Move to a dedicated study-protocol-parser service (beside the
// SSE code) once its home is decided.

/**
 * Presigns an upload slot, PUTs the file to S3, and resolves to the server-minted batch_api_id.
 * The server owns the S3 key (built from the team in its security context plus the IDs it mints);
 * the client only ever holds the opaque batch id, which it later sends to kick off the parser.
 */
const uploadProtocol = async (file: File, signal?: AbortSignal): Promise<AgentAttachmentBatchApiId> => {
  const presigned = await ApiService.call({
    endpoint: 'POST /api/v1/ai-agents/study-protocol-parser/uploads',
    body: { file_name: file.name },
    signal,
    options: {
      onError: { toast: false, throw: false, capture: true, slug: 'study-protocol-parser-presign-upload' },
    },
  });
  if (presigned.type !== ResponseType.Success) {
    throw new Error('Could not mint a presigned upload URL.');
  }
  const { url, batch_api_id } = presigned.body;

  // The presign call reports itself via ApiService; the direct-to-S3 PUT bypasses
  // it, so capture both its failure modes (network reject + non-2xx) to Sentry.
  let putResponse: Response;
  try {
    putResponse = await fetch(url, { method: 'PUT', body: file, signal });
  } catch (cause) {
    const error = new InVivoError('Study protocol S3 upload request failed', {
      cause: cause instanceof Error ? cause : new Error(String(cause)),
      slug: 'study-protocol-parser-s3-put',
      level: 'warning',
    });
    ExceptionHandler.captureException(error);
    throw error;
  }
  if (!putResponse.ok) {
    const error = new InVivoError(`Study protocol S3 upload failed (${putResponse.status})`, {
      slug: 'study-protocol-parser-s3-put',
      level: 'warning',
    });
    ExceptionHandler.captureException(error);
    throw error;
  }
  return batch_api_id;
};

/**
 * Agents-hub setup form for the Study Protocol Parser. Step one picks the study form the protocol
 * should be parsed against; step two uploads a .docx protocol to S3 (presign + PUT on file-add) and
 * stores the resulting batch id. Submitting kicks off the parser session and navigates to its
 * stream.
 */
const StudyProtocolParserSetupForm: FC = () => {
  const navigate = useNavigate();

  const { invoke: getForms } = useLegacyApiHook({
    method: 'GET',
    apiRoute: 'team.studies.forms.showAll',
    options: {
      invokeOnInit: false,
      onError: { toast: false, throw: false, capture: true, slug: 'study-protocol-parser-form-load' },
    },
  });

  // The forms list is small and fetched whole (as SelectStudyCreationForm does for the same
  // collection), so it is loaded once and filtered in memory — keystrokes must not re-hit the
  // endpoint.
  const formsCacheRef = useRef<Array<StudyForm> | null>(null);

  const loadForms = useCallback(
    async ({ inputValue }: { inputValue: string; page: number }): Promise<LoadOptionsResult<DatabaseId>> => {
      if (_isNil(formsCacheRef.current)) {
        const response = await getForms({ query: { perPage: -1 } });
        if (response.type !== ResponseType.Success) {
          // Already captured to Sentry; leave the field usable and empty rather than failing the step.
          return { options: [], hasMore: false };
        }
        formsCacheRef.current = (response.body as DataWrapper<Array<StudyForm>>).data ?? [];
      }

      const search = inputValue.trim().toLowerCase();
      const options = formsCacheRef.current.reduce<LoadOptionsResult<DatabaseId>['options']>((acc, form) => {
        if (form.name.toLowerCase().includes(search)) {
          acc.push({ label: form.name, value: Number(form.id) });
        }
        return acc;
      }, []);

      return { options, hasMore: false };
    },
    [getForms]
  );

  const handleSubmit = useCallback(
    async (values: StudyProtocolParserSetupValues): Promise<void> => {
      // The required rules block submit while either field is unset; guard anyway so a null shape
      // can never reach the request body.
      if (_isNil(values.protocol_batch_api_id) || _isNil(values.form_id)) {
        return;
      }
      const response = await ApiService.call({
        endpoint: 'POST /api/v1/ai-agents/study-protocol-parser',
        body: {
          batch_api_id: values.protocol_batch_api_id,
          form_id: values.form_id,
        },
        options: {
          onError: {
            toast_message: 'Failed to start the protocol parser. Please try again.',
            throw: false,
            capture: true,
            slug: 'study-protocol-parser-create-session',
          },
        },
      });
      if (response.type === ResponseType.Success) {
        const sessionApiId = response.body.api_id;
        navigate(webRoute('agents.session', { slug: agentUrlSlugMap.study_protocol_parser, sessionApiId }));
      }
    },
    [navigate]
  );

  const steps: Array<StepConfig> = useMemo(
    () => [
      {
        id: 'form',
        title: 'Select a form',
        required: true,
        fields: [
          {
            type: 'select',
            name: 'form_id',
            label: 'Study form',
            placeholder: 'Search for a form...',
            loadOptions: loadForms,
            rules: { required: 'A form is required' },
          },
        ],
      },
      {
        id: 'protocol',
        title: 'Upload protocol',
        required: true,
        fields: [
          {
            type: 'upload',
            name: 'protocol_batch_api_id',
            label: 'Protocol document',
            accept: DOCX_ACCEPT,
            helpText: 'Supported formats: .docx',
            uploadFile: uploadProtocol,
            rules: { required: 'A protocol document is required' },
          },
        ],
      },
    ],
    [loadForms]
  );

  return (
    <VerticalStepForm<StudyProtocolParserSetupValues>
      steps={steps}
      onSubmit={handleSubmit}
      description="Select a form and upload a study protocol document to parse it into a draft study"
      submitDescription="Will extract study data, groups, and animals from the uploaded protocol."
    />
  );
};

export default StudyProtocolParserSetupForm;
